import { useForm, usePage } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Link } from '@inertiajs/react'

type CaseRecord = {
  id: string
  state_code: string
  entity_type_code: string
  filing_type: string
  legal_name: string
  lifecycle_state: string
}

type Props = {
  cases: CaseRecord[]
}

const ENTITY_TYPES = [
  'llc',
  'c_corp',
  's_corp',
  'nonprofit',
  'lp',
  'llp',
  'lllp',
  'pllc',
  'pc',
]

const FILING_TYPES = [
  { value: 'formation', label: 'Formation' },
  { value: 'foreign_qualification', label: 'Foreign Qualification' },
]

const STATES = ['DE', 'WY', 'FL', 'TX', 'CA']

export default function BusinessRegistrationUsCasesIndex({ cases }: Props) {
  const page = usePage()
  const accountSlug = (page.props as { auth?: { account?: { slug?: string } } }).auth?.account?.slug

  const { data, setData, post, processing, errors, reset } = useForm({
    case: {
      state_code: 'DE',
      entity_type_code: 'llc',
      filing_type: 'formation',
      legal_name: '',
      assumed_name: '',
      interview_payload: {},
    },
  })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    post(`/${accountSlug}/business-registration/us/cases`, {
      onSuccess: () => {
        reset()
      },
    })
  }

  return (
    <DashboardLayout title="US Business Registration">
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Create US Registration Case</CardTitle>
            <CardDescription>
              Start a new filing workflow with jurisdiction-specific interview, principals, EIN, e-sign, and payment.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form className="grid md:grid-cols-2 gap-4" onSubmit={submit}>
              <div className="space-y-2">
                <Label>State</Label>
                <Select
                  value={data.case.state_code}
                  onValueChange={(value) => setData('case', { ...data.case, state_code: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATES.map((state) => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Entity Type</Label>
                <Select
                  value={data.case.entity_type_code}
                  onValueChange={(value) => setData('case', { ...data.case, entity_type_code: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((entityType) => (
                      <SelectItem key={entityType} value={entityType}>{entityType}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Filing Type</Label>
                <Select
                  value={data.case.filing_type}
                  onValueChange={(value) => setData('case', { ...data.case, filing_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILING_TYPES.map((filingType) => (
                      <SelectItem key={filingType.value} value={filingType.value}>{filingType.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Legal Name</Label>
                <Input
                  value={data.case.legal_name}
                  onChange={(event) => setData('case', { ...data.case, legal_name: event.target.value })}
                  placeholder="Acme Holdings LLC"
                  required
                />
                {errors['case.legal_name'] && <p className="text-sm text-destructive">{errors['case.legal_name']}</p>}
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Assumed Name (optional)</Label>
                <Input
                  value={data.case.assumed_name}
                  onChange={(event) => setData('case', { ...data.case, assumed_name: event.target.value })}
                  placeholder="Acme"
                />
              </div>

              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" disabled={processing}>{processing ? 'Creating...' : 'Create Case'}</Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>US Cases</CardTitle>
            <CardDescription>Manage in-progress and completed registration workflows.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {cases.length === 0 ? (
              <p className="text-sm text-muted-foreground">No US registration cases yet.</p>
            ) : (
              cases.map((caseRecord) => (
                <div key={caseRecord.id} className="border rounded-lg p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{caseRecord.legal_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {caseRecord.state_code} • {caseRecord.entity_type_code} • {caseRecord.filing_type}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{caseRecord.lifecycle_state}</Badge>
                    <Link
                      href={`/${accountSlug}/business-registration/us/cases/${caseRecord.id}`}
                      className="text-sm underline"
                    >
                      Open Workflow
                    </Link>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
